const SEMANTIC_API_BASE = 'https://swiss-parliament-api.innoautomation.ch';
const DB_API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8000';

export interface PaginatedResponse<T> {
  results: T[];
  total_count: number;
  has_more: boolean;
  status_info?: string;
}

/**
 * Error thrown by fetchApi / mutateApi on a non-2xx response. Carries the
 * HTTP `status` so callers can branch — e.g. the React Query retry predicate
 * (retry 5xx/network, never 4xx) and error UIs that distinguish "not found"
 * from "server down". `status` is 0 for network/transport failures where no
 * response was received.
 */
export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

// Endpoints that must go to the external semantic API
const SEMANTIC_ROUTES = [
  '/v1/affairs/search',
  '/v1/affairs/related',
  // '/v1/transcripts' — now served by local backend via /db/transcripts (Meilisearch)
];

function resolveUrl(endpoint: string): string {
  if (endpoint === '/health') return `${DB_API_BASE}/health`;

  const isSemantic = SEMANTIC_ROUTES.some(r => endpoint.startsWith(r));
  if (isSemantic) return `${SEMANTIC_API_BASE}${endpoint}`;

  // All other /v1/ routes map to local /db/
  return `${DB_API_BASE}${endpoint.replace('/v1/', '/db/')}`;
}

// Optional auth: callers that need to send an Authorization header (e.g.
// the chat surface, post-Audit-C1) pass a getToken callback. We fetch the
// token at call time so it stays fresh — Clerk's getToken returns the
// current session JWT and rotates it automatically near expiry.
export type GetToken = () => Promise<string | null | undefined>;

interface RequestOpts {
  /** Async accessor for the Clerk session JWT. If provided and resolves
   *  to a non-empty string, attached as `Authorization: Bearer <token>`. */
  getToken?: GetToken;
}

async function _authHeaders(opts?: RequestOpts): Promise<Record<string, string>> {
  if (!opts?.getToken) return {};
  try {
    const t = await opts.getToken();
    return t ? { Authorization: `Bearer ${t}` } : {};
  } catch {
    return {};
  }
}

export async function mutateApi<T>(
  endpoint: string,
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  body: unknown,
  opts?: RequestOpts,
): Promise<T> {
  let response: Response;
  try {
    response = await fetch(resolveUrl(endpoint), {
      method,
      headers: { 'Content-Type': 'application/json', ...(await _authHeaders(opts)) },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch (e) {
    throw new ApiError(0, `Network error: ${(e as Error).message}`);
  }
  if (!response.ok) {
    throw new ApiError(response.status, `API error: ${response.status} ${response.statusText}`);
  }
  return response.json();
}

export async function fetchApi<T>(
  endpoint: string,
  params: Record<string, string | number | boolean | undefined | null> = {},
  opts?: RequestOpts,
): Promise<T> {
  const urlStr = resolveUrl(endpoint);
  const url = new URL(urlStr);

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      url.searchParams.append(key, String(value));
    }
  });

  let response: Response;
  try {
    response = await fetch(url.toString(), {
      headers: await _authHeaders(opts),
    });
  } catch (e) {
    throw new ApiError(0, `Network error: ${(e as Error).message}`);
  }
  if (!response.ok) {
    throw new ApiError(response.status, `API error: ${response.status} ${response.statusText}`);
  }
  return response.json();
}
